import { authFetch } from "@/lib/api-client";

export type LlmProvider = "GEMINI" | "ANTHROPIC";

/**
 * Cost from the rates in Admin → Settings, in micro-dollars (millionths of a
 * dollar). Always read alongside `unpricedTokens`: models with no rate set
 * contribute nothing, so a cost shown on its own understates the bill.
 */
export interface LlmUsageCost {
  costMicros: number;
  /** Tokens from models with no price set, and so missing from `costMicros`. */
  unpricedTokens: number;
}

export interface LlmUsageModelTotals {
  provider: LlmProvider;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  /** Gemini's `thoughtsTokenCount`. Billed, and can dwarf visible output. */
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  avgLatencyMs: number | null;
  costMicros: number;
  /** False when this model has no rate set, which is why its cost is zero. */
  isPriced: boolean;
}

/**
 * Enough tenant identity to draw the same logo mark the rest of the app draws.
 * `logoUrl` is already resolved by the API, so it goes straight into an `img`.
 */
export interface LlmUsageTenantBranding {
  tenantName: string | null;
  brandColor: string | null;
  logoUrl: string | null;
}

/**
 * Spend per tenant, ranked highest first. `tenantId` is null for platform-level
 * work no tenant asked for; `tenantName` is null only when the tenant row is
 * gone, in which case the table falls back to showing the id.
 */
export interface LlmUsageTenantTotals extends LlmUsageCost, LlmUsageTenantBranding {
  tenantId: string | null;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  avgLatencyMs: number | null;
}

/**
 * Spend per product surface, ranked highest first. `feature` is null for calls
 * recorded before their call site was given an attribution.
 */
export interface LlmUsageFeatureTotals extends LlmUsageCost {
  feature: string | null;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  avgLatencyMs: number | null;
}

/** Rolling-window aggregates behind the token-usage KPI strip. */
export interface LlmUsageStats extends LlmUsageCost {
  windowHours: number;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  avgLatencyMs: number | null;
  p95LatencyMs: number | null;
  byModel: LlmUsageModelTotals[];
  byTenant: LlmUsageTenantTotals[];
  byFeature: LlmUsageFeatureTotals[];
}

export interface LlmUsageFilters {
  /**
   * Rolling window the server resolves against its own clock, so the filters
   * stay a stable cache key. Omitted means the API's default window.
   */
  windowHours?: number;
  provider?: LlmProvider;
  tenantId?: string;
  /** One of `LLM_USAGE_FEATURES` — the product surface that made the call. */
  feature?: string;
}

/** One model's share of a bucket, for the chart's stack-by-model view. */
export interface LlmUsageBucketModel {
  provider: LlmProvider;
  model: string;
  totalTokens: number;
}

/** One feature's share of a bucket. Null covers calls with no attribution. */
export interface LlmUsageBucketFeature {
  feature: string | null;
  totalTokens: number;
}

/** One tenant's share of a bucket. Null covers platform-level work. */
export interface LlmUsageBucketTenant {
  tenantId: string | null;
  tenantName: string | null;
  totalTokens: number;
}

/** A tenant that spent something in the window, for the filter control. */
export interface LlmUsageTenantOption extends LlmUsageTenantBranding {
  tenantId: string;
}

export interface LlmUsageBucket {
  /** ISO start of the bucket; it spans `bucketMinutes` from there. */
  bucketStart: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  thinkingTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  /** Null for a bucket nothing landed in, which the chart draws as a gap. */
  p95LatencyMs: number | null;
  /** Only the models that ran in this bucket; a quiet bucket carries none. */
  models: LlmUsageBucketModel[];
  /** Only the features that ran in this bucket; a quiet bucket carries none. */
  features: LlmUsageBucketFeature[];
  /** Only the tenants that ran in this bucket; a quiet bucket carries none. */
  tenants: LlmUsageBucketTenant[];
}

export interface LlmUsageTimeseries {
  windowHours: number;
  bucketMinutes: number;
  /** Gapless and ascending: a quiet hour is a zero, never a missing bar. */
  buckets: LlmUsageBucket[];
}

export interface VertexModelUsage {
  model: string;
  location: string;
  publisher: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/**
 * What Vertex itself reports, from Cloud Monitoring. `available: false` means
 * Monitoring could not be read — a missing IAM role or an unreachable API —
 * which is a normal state for this panel rather than a failed request.
 */
export interface VertexUsage {
  available: boolean;
  windowHours: number;
  byModel: VertexModelUsage[];
  buckets: Array<{
    startTime: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  }>;
}

function usageParams(filters: LlmUsageFilters): string {
  const params = new URLSearchParams();
  if (filters.windowHours) params.set("windowHours", String(filters.windowHours));
  if (filters.provider) params.set("provider", filters.provider);
  if (filters.tenantId) params.set("tenantId", filters.tenantId);
  if (filters.feature) params.set("feature", filters.feature);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export async function fetchLlmUsageStats(filters: LlmUsageFilters): Promise<LlmUsageStats> {
  const { data } = await authFetch<LlmUsageStats>(`/admin/llm-usage/stats${usageParams(filters)}`);
  return data;
}

export async function fetchLlmUsageTimeseries(
  filters: LlmUsageFilters,
): Promise<LlmUsageTimeseries> {
  const { data } = await authFetch<LlmUsageTimeseries>(
    `/admin/llm-usage/timeseries${usageParams(filters)}`,
  );
  return data;
}

export async function fetchLlmUsageFeatures(windowHours?: number): Promise<string[]> {
  const query = windowHours ? `?windowHours=${windowHours}` : "";
  const { data } = await authFetch<string[]>(`/admin/llm-usage/features${query}`);
  return data;
}

export async function fetchLlmUsageTenants(windowHours?: number): Promise<LlmUsageTenantOption[]> {
  const query = windowHours ? `?windowHours=${windowHours}` : "";
  const { data } = await authFetch<LlmUsageTenantOption[]>(`/admin/llm-usage/tenants${query}`);
  return data;
}

export async function fetchVertexUsage(windowHours?: number): Promise<VertexUsage> {
  const query = windowHours ? `?windowHours=${windowHours}` : "";
  const { data } = await authFetch<VertexUsage>(`/admin/llm-usage/vertex${query}`);
  return data;
}
